import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  NumberInput,
  Pagination,
  Paper,
  ScrollArea,
  SimpleGrid,
  SegmentedControl,
  Select,
  Stack,
  Anchor,
  Textarea,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconAlertTriangle, IconPencil } from "@tabler/icons-react";
import { FaSync } from "react-icons/fa";
import { showNotification } from "@mantine/notifications";
import { ApiService } from "../../api/api-service";

interface BridgeSession {
  id: string;
  batchNo: string;
  students: string[];
  trainer: string;
  admin: string;
  meetingLink: string;
  country: string;
  batchTiming: string;
  numSessions: number;
  gmAccountName: string;
  scheduleStatus: string;
  orientation: string;
  bridge: string;
  liveProjectsTiming: string;
  resume: string;
  linkedinGrooming: string;
  feedback: string;
  remark: string;
  nextAction: string;
  agentName: string;
  batchStatus: string;
}

type EditableField =
  | "agentName"
  | "nextAction"
  | "country"
  | "liveProjectsTiming"
  | "resume"
  | "linkedinGrooming"
  | "feedback"
  | "remark"
  | "orientation"
  | "bridge";

type EditForm = Pick<BridgeSession, EditableField>;

// The session count at which a row gets the alert icon.
const ALERT_SESSION_COUNT = 5;
// Batches numbered below this are hidden. The user can change it in the modal.
const DEFAULT_MIN_BATCH_NO = 4000;
const PAGE_SIZES = ["10", "25", "50", "100"];
const MIN_BATCH_STORAGE_KEY = "bridgeSessionMinBatchNo";
const NEXT_ACTIONS = ["Pending", "Orientation Scheduled", "Bridge Scheduled", "Completed"];
const YES_NO = ["Yes", "No"];
const CATEGORIES = ["All", "Orientation", "Bridge", "Completed"];

const readMinBatchNo = (): number => {
  try {
    const stored = Number(localStorage.getItem(MIN_BATCH_STORAGE_KEY));
    return stored > 0 ? stored : DEFAULT_MIN_BATCH_NO;
  } catch {
    return DEFAULT_MIN_BATCH_NO;
  }
};

interface Props {
  opened: boolean;
  onClose: () => void;
}

const ReadOnlyField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <Text size="xs" c="dimmed">{label}</Text>
    <Text size="sm" fw={500}>{children || "-"}</Text>
  </div>
);

const BridgeSessionModal: React.FC<Props> = ({ opened, onClose }) => {
  const [rows, setRows] = useState<BridgeSession[]>([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [minBatchNo, setMinBatchNo] = useState<number>(readMinBatchNo);
  const [editing, setEditing] = useState<BridgeSession | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("25");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiService.getInstance().get("/bridge-sessions", {
        params: { minBatchNo },
      });
      setRows(res.data?.data ?? []);
    } catch (error) {
      showNotification({ color: "red", message: "Failed to load bridge sessions" });
    } finally {
      setLoading(false);
    }
  }, [minBatchNo]);

  useEffect(() => {
    if (opened) load();
  }, [opened, load]);

  const changeMinBatchNo = (value: string | number) => {
    const n = Number(value);
    if (!n || n < 1) return;
    setMinBatchNo(n);
    try {
      localStorage.setItem(MIN_BATCH_STORAGE_KEY, String(n));
    } catch {
      /* storage unavailable: the value still applies for this session */
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await ApiService.getInstance().post("/bridge-sessions/refresh", { minBatchNo });
      await load();
      showNotification({ color: "green", message: "Synced with the latest batch data" });
    } catch (error) {
      showNotification({ color: "red", message: "Refresh failed. Try again." });
    } finally {
      setRefreshing(false);
    }
  };

  const openEdit = (row: BridgeSession) => {
    setEditing(row);
    setForm({
      agentName: row.agentName || "",
      nextAction: row.nextAction || "Pending",
      country: row.country || "",
      liveProjectsTiming: row.liveProjectsTiming || "",
      resume: row.resume || "No",
      linkedinGrooming: row.linkedinGrooming || "No",
      feedback: row.feedback || "",
      remark: row.remark || "",
      orientation: row.orientation || "No",
      bridge: row.bridge || "No",
    });
  };

  const setField = (field: EditableField, value: string) =>
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));

  const saveEdit = async () => {
    if (!editing || !form) return;
    setSaving(true);
    try {
      await ApiService.getInstance().patch(
        `/bridge-sessions/${encodeURIComponent(editing.id)}`,
        form
      );
      setRows((prev) => prev.map((r) => (r.id === editing.id ? { ...r, ...form } : r)));
      showNotification({ color: "green", message: `Saved ${editing.batchNo}` });
      setEditing(null);
    } catch (error) {
      showNotification({ color: "red", message: `Could not save ${editing.batchNo}` });
    } finally {
      setSaving(false);
    }
  };

  const inCategory = (r: BridgeSession, c: string) =>
    c === "Orientation"
      ? r.orientation === "Yes"
      : c === "Bridge"
      ? r.bridge === "Yes"
      : c === "Completed"
      ? (r.nextAction || "").toLowerCase() === "completed"
      : true;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        inCategory(r, category) &&
        (!q || `${r.batchNo} ${r.students.join(" ")} ${r.trainer}`.toLowerCase().includes(q))
    );
  }, [rows, category, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / Number(pageSize)));
  const pageRows = useMemo(
    () => visible.slice((page - 1) * Number(pageSize), page * Number(pageSize)),
    [visible, page, pageSize]
  );

  // Back to the first page whenever the filtered list changes
  useEffect(() => {
    setPage(1);
  }, [category, search, minBatchNo, pageSize]);

  const count = (c: string) => rows.filter((r) => inCategory(r, c)).length;

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={<Text fw={600}>Bridge Sessions</Text>}
        size="90%"
        centered
      >
        <Stack gap="md" pos="relative">
          <LoadingOverlay visible={loading} />
          <Group justify="space-between">
            <SegmentedControl
              size="xs"
              value={category}
              onChange={setCategory}
              data={CATEGORIES.map((c) => ({ value: c, label: `${c} (${count(c)})` }))}
            />
            <Group gap="xs">
              <NumberInput
                size="xs"
                w={150}
                label="Show batch no. from"
                value={minBatchNo}
                min={1}
                hideControls
                onChange={changeMinBatchNo}
              />
              <TextInput
                size="xs"
                label="Search"
                placeholder="Batch, student, trainer"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                w={200}
              />
              <Tooltip label="Fetch the latest number of sessions and details from the batches" withArrow>
                <Button
                  size="xs"
                  variant="light"
                  mt={20}
                  loading={refreshing}
                  leftSection={<FaSync size={11} />}
                  onClick={refresh}
                >
                  Refresh
                </Button>
              </Tooltip>
            </Group>
          </Group>

          <ScrollArea h="62vh">
            <Table striped withTableBorder stickyHeader verticalSpacing="xs" fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Batch no.</Table.Th>
                  <Table.Th>Student</Table.Th>
                  <Table.Th>Trainer</Table.Th>
                  <Table.Th>Batch timing</Table.Th>
                  <Table.Th>Sessions</Table.Th>
                  <Table.Th>Meeting link</Table.Th>
                  <Table.Th>Orientation</Table.Th>
                  <Table.Th>Bridge</Table.Th>
                  <Table.Th>Next action</Table.Th>
                  <Table.Th>Agent</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {!loading && visible.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={11}>
                      <Text ta="center" c="dimmed" py="md">
                        No on-going batches from {minBatchNo} onwards. Click Refresh to pull in the latest batches.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {pageRows.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td fw={600}>{r.batchNo}</Table.Td>
                    <Table.Td>{r.students.join(", ")}</Table.Td>
                    <Table.Td>{r.trainer}</Table.Td>
                    <Table.Td>{r.batchTiming}</Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Text size="xs" fw={600}>{r.numSessions}</Text>
                        {r.numSessions === ALERT_SESSION_COUNT && (
                          <Tooltip label={`Batch has reached ${ALERT_SESSION_COUNT} sessions`} withArrow>
                            <ActionIcon variant="transparent" color="red" size="sm" aria-label="Session alert">
                              <IconAlertTriangle size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td maw={220}>
                      {r.meetingLink ? (
                        <Anchor href={r.meetingLink} target="_blank" size="xs" truncate="end" display="block">
                          {r.meetingLink.replace(/^https?:\/\//, "")}
                        </Anchor>
                      ) : (
                        <Text c="dimmed" size="xs">No link</Text>
                      )}
                    </Table.Td>
                    <Table.Td>{r.orientation || "No"}</Table.Td>
                    <Table.Td>{r.bridge || "No"}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={(r.nextAction || "").toLowerCase() === "completed" ? "green" : "gray"}>
                        {r.nextAction || "Pending"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{r.agentName}</Table.Td>
                    <Table.Td>
                      <Tooltip label="Edit details" withArrow>
                        <ActionIcon variant="subtle" aria-label={`Edit ${r.batchNo}`} onClick={() => openEdit(r)}>
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          <Group justify="space-between">
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                {visible.length === 0
                  ? "0 batches"
                  : `${(page - 1) * Number(pageSize) + 1}-${Math.min(page * Number(pageSize), visible.length)} of ${visible.length}`}
              </Text>
              <Select size="xs" w={90} data={PAGE_SIZES} value={pageSize} allowDeselect={false} onChange={(v) => v && setPageSize(v)} aria-label="Rows per page" />
            </Group>
            <Pagination size="sm" total={totalPages} value={page} onChange={setPage} />
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!editing}
        onClose={() => setEditing(null)}
        title={<Text fw={600}>Edit {editing?.batchNo}</Text>}
        size="lg"
        centered
      >
        {editing && form && (
          <Stack gap="md">
            <Paper withBorder p="sm" bg="var(--mantine-color-gray-light)">
              <Text size="xs" fw={600} c="dimmed" mb="xs">FROM THE BATCH (read-only, use Refresh to update)</Text>
              <SimpleGrid cols={{ base: 2, sm: 3 }}>
                <ReadOnlyField label="Student">{editing.students.join(", ")}</ReadOnlyField>
                <ReadOnlyField label="Trainer">{editing.trainer}</ReadOnlyField>
                <ReadOnlyField label="Admin">{editing.admin}</ReadOnlyField>
                <ReadOnlyField label="Batch timing">{editing.batchTiming}</ReadOnlyField>
                <ReadOnlyField label="Sessions">{String(editing.numSessions)}</ReadOnlyField>
                <ReadOnlyField label="Schedule type">{editing.scheduleStatus}</ReadOnlyField>
                <ReadOnlyField label="GM account">{editing.gmAccountName}</ReadOnlyField>
                <ReadOnlyField label="Batch status">{editing.batchStatus}</ReadOnlyField>
                <ReadOnlyField label="Meeting link">{editing.meetingLink}</ReadOnlyField>
              </SimpleGrid>
            </Paper>

            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput label="Agent name (takes the bridge session)" value={form.agentName} onChange={(e) => setField("agentName", e.currentTarget.value)} />
              <Select label="Next action" data={NEXT_ACTIONS} value={form.nextAction} allowDeselect={false} onChange={(v) => v && setField("nextAction", v)} />
              <TextInput label="Country" value={form.country} onChange={(e) => setField("country", e.currentTarget.value)} />
              <TextInput label="Live projects timing" placeholder="e.g. Sat 10 AM" value={form.liveProjectsTiming} onChange={(e) => setField("liveProjectsTiming", e.currentTarget.value)} />
              <Select label="Orientation" data={YES_NO} value={form.orientation} allowDeselect={false} onChange={(v) => v && setField("orientation", v)} />
              <Select label="Bridge" data={YES_NO} value={form.bridge} allowDeselect={false} onChange={(v) => v && setField("bridge", v)} />
              <Select label="Resume" data={YES_NO} value={form.resume} allowDeselect={false} onChange={(v) => v && setField("resume", v)} />
              <Select label="LinkedIn profile grooming" data={YES_NO} value={form.linkedinGrooming} allowDeselect={false} onChange={(v) => v && setField("linkedinGrooming", v)} />
            </SimpleGrid>
            <Textarea label="Feedback (from the customer)" autosize minRows={2} value={form.feedback} onChange={(e) => setField("feedback", e.currentTarget.value)} />
            <Textarea label="Remark" autosize minRows={2} value={form.remark} onChange={(e) => setField("remark", e.currentTarget.value)} />

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditing(null)}>Cancel</Button>
              <Button loading={saving} onClick={saveEdit}>Save</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
};

export default BridgeSessionModal;
